import type { Appearance, Preferences } from '@beyond-js/ui';
import { Observable } from '@cdn/state';

interface Specs {
	/** The person's language and appearance (decision D07), which paint `data-beyond-mode` */
	preferences: Preferences;
	/** true when the system prefers a dark scheme */
	dark?: boolean;
}

/**
 * Light or dark presentation, held by the family's `Preferences`: the person's account decides it on arrival,
 * a choice made here applies on this device until the account's values change, and without either the system's scheme
 * applies (this application's default). `mode` is what is shown; `appearance` is what was chosen, `system`
 * included.
 */
export class Theme extends Observable {
	#preferences: Preferences;
	#dark: boolean;
	#release: () => void;

	get appearance(): Appearance {
		return this.#preferences.appearance;
	}

	/** The family's `Preferences` that hold the language and the appearance, for the bar's dialog */
	get preferences(): Preferences {
		return this.#preferences;
	}

	get mode(): 'light' | 'dark' {
		const appearance = this.appearance;
		return appearance === 'system' ? (this.#dark ? 'dark' : 'light') : appearance;
	}

	constructor({ preferences, dark = false }: Specs) {
		super();
		this.#preferences = preferences;
		this.#dark = dark;
		let appearance = this.appearance;
		this.#release = preferences.subscribe(values => {
			if (values.appearance === appearance) return;
			appearance = values.appearance;
			this.changed();
		});
	}

	/** The other of light and dark, on this device only */
	toggle() {
		this.#preferences.choose({ appearance: this.mode === 'dark' ? 'light' : 'dark' });
	}

	destroy() {
		this.#release();
	}
}
